import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProfileBlock from "./profile/Profile";
import BurgerMenuBlock from "./burgerMenu/BurgerMenu";
import Deposit from "./depositBlock/Deposit";
import { IconDownload, IconMenu, IconUser } from "../profile/icons";
import { useKycStatus, useMe } from "../profile/useProfileApi";
import "./Header.css";

const norm = (p) => p.replace(/\/+$/, "") || "/";

// deposit / hasMenu / hasMenu2 оставлены в сигнатуре, чтобы не ломать существующие вызовы <Header hasMenu2 />
// eslint-disable-next-line no-unused-vars
const Header = ({ deposit = false, hasMenu = false, hasMenu2 = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { me, loading: meLoading, error: meError } = useMe();
  const kyc = useKycStatus();

  // null | "profile" | "burger" | "deposit" | "withdrawal"
  const [layer, setLayer] = useState(null);
  // тип модалки запоминаем, чтобы содержимое не пропадало во время закрытия
  const [modalType, setModalType] = useState("deposit");

  const openSpecificMenu = useCallback((menu) => {
    if (menu === "deposit" || menu === "withdrawal") setModalType(menu);
    setLayer(menu || null);
  }, []);
  const close = useCallback(() => setLayer(null), []);

  // Esc закрывает, скролл страницы под оверлеем блокируется
  useEffect(() => {
    if (!layer) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [layer, close]);

  // при смене маршрута панели закрываются
  useEffect(() => {
    setLayer(null);
  }, [location.pathname]);

  const path = norm(location.pathname);
  const tabs = [
    {
      key: "trade",
      to: "/market?symbol=BTCUSDT",
      match: "/market",
      label: t("header.trade"),
    },
    {
      key: "assets",
      to: "/account/",
      match: "/account",
      label: t("header.assets"),
    },
    {
      key: "portfolio",
      to: "/account/portfolio",
      match: "/account/portfolio",
      label: t("header.portfolio"),
    },
  ];

  const initials =
    ((me?.name?.[0] ?? "") + (me?.surname?.[0] ?? "")).toUpperCase() ||
    (me?.email?.[0] ?? "").toUpperCase();

  const modalOpen = layer === "deposit" || layer === "withdrawal";

  return (
    <>
      <header className="fxh header">
        <div className="fxh-left">
          <Link
            to={me ? "/market?symbol=BTCUSDT" : "/"}
            className="fxh-logo"
            aria-label="FYNEX"
            onClick={() => (window.location.href = me ? "/market?symbol=BTCUSDT" : "/")}
          >
            <span className="fxh-logo-mark" />
            <span className="fxh-logo-mark" />
            <b>FYNEX</b>
          </Link>

          {me && (
            <nav
              className="fxh-tabs"
              aria-label={t("header.nav", "Основная навигация")}
            >
              {tabs.map((tab) => {
                const on = path === norm(tab.match);
                return (
                  <Link
                    key={tab.key}
                    to={tab.to}
                    className={on ? "on" : ""}
                    aria-current={on ? "page" : undefined}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>

        <div className="fxh-acts">
          {me && (
            <>
              <button
                type="button"
                className="fxh-btn fxh-btn-primary"
                onClick={() => openSpecificMenu("deposit")}
              >
                <IconDownload />
                <span>{t("header.deposit")}</span>
              </button>

              <button
                type="button"
                className="fxh-avatar"
                aria-label={t("profile.menu")}
                aria-haspopup="dialog"
                aria-expanded={layer === "profile"}
                onClick={() => openSpecificMenu("profile")}
              >
                <svg
                  width={11}
                  height={15}
                  viewBox="0 0 11 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_3_2)">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M2.46133 3.46155C2.46133 2.6659 2.7774 1.90284 3.34 1.34023C3.90261 0.777618 4.66568 0.461548 5.46132 0.461548C6.25697 0.461548 7.02003 0.777618 7.58264 1.34023C8.14525 1.90284 8.46132 2.6659 8.46132 3.46155C8.46132 4.25719 8.14525 5.02026 7.58264 5.58286C7.02003 6.14547 6.25697 6.46154 5.46132 6.46154C4.66568 6.46154 3.90261 6.14547 3.34 5.58286C2.7774 5.02026 2.46133 4.25719 2.46133 3.46155ZM-0.038006 12.8649C-0.015526 11.4212 0.57375 10.0442 1.60263 9.03126C2.63151 8.01828 4.01747 7.45051 5.46132 7.45051C6.90518 7.45051 8.29114 8.01828 9.32 9.03126C10.3489 10.0442 10.9382 11.4212 10.9607 12.8649C10.9624 12.9621 10.9358 13.0577 10.884 13.1399C10.8323 13.2222 10.7577 13.2877 10.6693 13.3282C9.0353 14.077 7.25872 14.4636 5.46132 14.4615C3.60399 14.4615 1.83933 14.0562 0.25333 13.3282C0.16498 13.2877 0.09038 13.2222 0.03864 13.1399C-0.013107 13.0577 -0.03974 12.9621 -0.038006 12.8649Z"
                      fill="#9AAAA3"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3_2">
                      <rect width={11} height={15} fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </>
          )}

          <button
            type="button"
            className="fxh-icon-btn"
            aria-label={t("burger_menu.setting")}
            aria-haspopup="dialog"
            aria-expanded={layer === "burger"}
            onClick={() => openSpecificMenu("burger")}
          >
            <IconMenu />
          </button>
        </div>
      </header>

      <div className={`fxh-layer${layer ? " open" : ""}`} onClick={close}>
        <ProfileBlock
          open={layer === "profile"}
          onClose={close}
          openSpecificMenu={openSpecificMenu}
          me={me}
          meLoading={meLoading}
          meError={meError}
          kycStatus={kyc.status}
        />
        <BurgerMenuBlock open={layer === "burger"} onClose={close} />
        <Deposit open={modalOpen} type={modalType} onClose={close} />
      </div>
    </>
  );
};

export default Header;
